import {useCallback, useMemo, useState} from 'react';
import {SHOP_CHEST_CONFIGS} from '../../configs/chests';
import {CHESTS} from '../../constants/chests';
import {useFactionPurchase} from './useFactionPurchase';
import {useChestPurchase} from '../shop/useChestPurchase';

/**
 * Покупка закрытой фракции прямо из выбора фракции — общая для экрана выбора
 * (FactionSelectPage) и встроенного выбора десктопного лобби
 * (LobbyFactionPicker). Уже реализованная, но не открытая фракция покупается
 * за кристаллы (useFactionPurchase, как в магазине); еще не реализованная —
 * кнопкой «Получить», открывающей Сундук Вечности (useChestPurchase).
 * Модалки к этим состояниям — FactionSelectPurchaseModals.
 *
 * @param {Object} auth - private.auth
 */
export const useFactionSelectPurchase = (auth) => {
  const [isEternalChestOpen, setIsEternalChestOpen] = useState(false);

  const availableFactions = auth.availableFactions || [];
  const isEternalOpened = Boolean(auth.isAllFactionsAvailable);

  // Кнопка покупки заблокированной, но уже реализованной фракции — та же
  // логика, что и в магазине (см. useFactionPurchase/ShopPage.js)
  const {
    handleBuyFaction,
    purchasedFactionId,
    setPurchasedFactionId,
    notEnoughCrystalsAmount: factionNotEnoughCrystalsAmount,
    setNotEnoughCrystalsAmount: setFactionNotEnoughCrystalsAmount,
  } = useFactionPurchase(auth, availableFactions);

  // Кнопка «Получить» у ещё не реализованных фракций — открывает Сундук
  // Вечности (тот же buy-chest флоу, что и в магазине)
  const {
    handleBuyChest,
    notEnoughCrystalsAmount: chestNotEnoughCrystalsAmount,
    setNotEnoughCrystalsAmount: setChestNotEnoughCrystalsAmount,
  } = useChestPurchase(auth);

  const notEnoughCrystalsAmount = factionNotEnoughCrystalsAmount ?? chestNotEnoughCrystalsAmount;
  // useCallback — ссылка идет в onSelectedIndexChange карусели (эффект
  // FactionsCarousel зависит от нее самой, не только от индекса): без
  // мемоизации каждый ре-рендер (в т.ч. от setNotEnoughCrystalsAmount выше)
  // создавал бы новую функцию, эффект перезапускался бы и тут же стирал
  // только что выставленное состояние, не дав модалке показаться. Тот же
  // паттерн, что и handleSelectedIndexChange в FactionPage.js
  const clearNotEnoughCrystals = useCallback(() => {
    setFactionNotEnoughCrystalsAmount(null);
    setChestNotEnoughCrystalsAmount(null);
  }, [setFactionNotEnoughCrystalsAmount, setChestNotEnoughCrystalsAmount]);

  const eternalChestConfig = useMemo(
    () => SHOP_CHEST_CONFIGS.find((c) => c.id === CHESTS.ETERNAL),
    []
  );

  return {
    handleBuyFaction,
    purchasedFactionId,
    setPurchasedFactionId,
    handleBuyChest,
    notEnoughCrystalsAmount,
    clearNotEnoughCrystals,
    isEternalChestOpen,
    setIsEternalChestOpen,
    isEternalOpened,
    eternalChestConfig,
  };
};
